<template>
  <div class="lookup-scope">
    <div
      v-if="!hideOp"
      class="_op"
    >
      <m-select
        v-model:value="selectedType"
        :items="operatorTypes"
        :read-only="readOnly"
        :disabled="disabled"
        class="_select"
      />
    </div>
    <user-picker
      v-if="!isEmpty && property.lookupProperty.type === propertyType.user"
      v-model:value="selected"
      class="_user-select"
      nowrap
      :truncate-selected-items="maxTags"
      :read-only="readOnly"
      :disabled="disabled"
      :max-tag-text-length="maxTagTextLength"
    />
    <space-selector
      v-if="!isEmpty && property.lookupProperty.type === propertyType.space"
      v-model:value="selected"
      :read-only="readOnly"
      honor-route-context
      :disabled="disabled"
      :property-label="value.property.label"
      multiple
      :truncate="maxTags"
    />
    <m-select
      v-if="!isEmpty && [propertyType.options, propertyType.singleSelect].includes(property.lookupProperty.type)"
      v-model:value="selected"
      class="_select"
      nowrap
      :truncate-selected-items="maxTags"
      :items="options"
      keep-open-on-click
      return-object
      :placeholder="$t('optionScope.placeholder')"
      multiple
      tags
      automatic-color
      :read-only="readOnly"
      :disabled="disabled"
      :max-tag-text-length="maxTagTextLength"
    />
  </div>
</template>

<script>
import SpaceSelector from '@/components/spaces/SpaceSelector.vue';
import UserPicker from '@/components/UserPicker.vue';
import useScopeItem from '@/composables/filter/scope-item';
import { AND, EMPTY, NOT, NOT_EMPTY } from '@/lib/filter/scope-tree';
import { copy } from 'shared/lib/copy';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { propertyType } from 'shared/constants.json';
import { userScope as userScopeConfig } from 'shared/api/query/configs.json';

export default {
  name: 'LookupScope',
  props: {
    value: {
      type: Object,
      required: true,
    },
    isNot: {
      type: Boolean,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideOp: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      required: true,
    },
    property: {
      type: Object,
      required: true,
    },
    maxTagTextLength: {
      type: Number,
      default: 0,
    },
    maxTags: {
      type: Number,
      default: 2,
    },
  },
  emits: ['input', 'update:value', 'change-not', 'change'],
  components: { UserPicker, SpaceSelector },
  setup() {
    const { operatorTypes: getOperatorTypes } = useScopeItem();
    const operatorTypes = getOperatorTypes(propertyType.lookup);
    return { operatorTypes };
  },
  data() {
    return {
      selected: [],
      isEmpty: false,
      propertyType,
    };
  },
  computed: {
    scope() {
      if ([propertyType.user, propertyType.options, propertyType.singleSelect, propertyType.space].includes(this.property.lookupProperty.type)) {
        return {
          ...this.value,
          [this.fieldName]: this.selected,
          isEmpty: this.isEmpty,
        };
      }

      return {
        ...this.value,
        isEmpty: this.isEmpty,
      };
    },
    fieldName() {
      switch (this.property.lookupProperty.type) {
        case propertyType.user:
          return userScopeConfig.edges.users;
        case propertyType.space:
          return userScopeConfig.edges.spaces;
        case propertyType.options:
        case propertyType.singleSelect:
          return userScopeConfig.edges.selectedOptions;
        default:
          return '';
      }
    },
    selectedType: {
      get() {
        if (this.isNot) {
          if (this.isEmpty) {
            return NOT_EMPTY;
          }
          return NOT;
        }
        if (this.isEmpty) {
          return EMPTY;
        }
        return AND;
      },
      set(val) {
        this.updateEmpty(val);
        if ([NOT, NOT_EMPTY].includes(val)) {
          this.$emit('change-not', NOT);
          return;
        }
        this.$emit('change-not', AND);
      },
    },
  },
  methods: {
    updateEmpty(type) {
      if ([EMPTY, NOT_EMPTY].includes(type)) {
        this.isEmpty = true;
        return;
      }
      this.isEmpty = false;
    },
    updateValues() {
      let isEmpty = false;
      if (this.value !== null && this.value.isEmpty !== undefined) {
        isEmpty = this.value.isEmpty;
      }
      this.isEmpty = isEmpty;

      if (this.value === null || isNullOrUndefined(this.value[this.fieldName])) {
        this.selected = [];
        return;
      }
      this.selected = this.value[this.fieldName];
    },
    emitUpdate(val) {
      this.$emit('input', val);
      this.$emit('update:value', val);
      this.$emit('change', val);
    },
  },
  watch: {
    scope: {
      handler(newVal, oldVal) {
        if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
          return;
        }

        const cp = copy(newVal);
        this.emitUpdate(cp);
      },
      deep: true,
    },
    value: {
      handler() {
        this.updateValues();
      },
      deep: true,
    },
  },
  created() {
    this.updateValues();
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .lookup-scope {
    display: flex;

    ._op {
      margin-right: .8rem;

      ._select {
        max-width: 100%;
      }
    }

    ._goal-select {
      min-width: 20rem;
    }
  }
</style>
